/**
 * GovernmentAgenciesPage — `/government/agencies`.
 *
 * Catalogue grouped by agency PLUS a real-time 24h health snapshot via
 * `GET /v1/government/stats/agency-health`. UP/DEGRADED/DOWN status is
 * derived from REJECTED-vs-COMPLETED rate over the last 24h.
 */
import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  getAgencyHealth,
  listGovernmentServices,
  type AgencyHealthEntry,
} from '../../lib/api/government';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

function healthTone(status: AgencyHealthEntry['status']): { dot: string; text: string } {
  switch (status) {
    case 'UP':
      return { dot: 'bg-green-500', text: 'text-green-700' };
    case 'DEGRADED':
      return { dot: 'bg-amber-500', text: 'text-amber-700' };
    case 'DOWN':
      return { dot: 'bg-red-500', text: 'text-red-700' };
  }
}

export function GovernmentAgenciesPage(): JSX.Element {
  const services = useQuery({
    queryKey: ['gov', 'agencies'],
    queryFn: () => listGovernmentServices({ pageSize: 100 }),
    retry: false,
  });
  const health = useQuery({
    queryKey: ['gov', 'agency-health'],
    queryFn: getAgencyHealth,
    retry: false,
    staleTime: 15_000,
  });

  const denied =
    services.isError && services.error instanceof ApiError && (services.error.status === 401 || services.error.status === 403);

  const byAgency: Record<string, number> = {};
  for (const s of services.data?.items ?? []) {
    byAgency[s.agency] = (byAgency[s.agency] ?? 0) + 1;
  }
  const healthMap: Record<string, AgencyHealthEntry | undefined> = {};
  for (const h of health.data ?? []) healthMap[h.agency] = h;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agency integrations"
        description="Service catalogue grouped by partner MDA + 24h health snapshot."
      />

      {services.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Government/readCatalog</code>.
        </DeferredNotice>
      ) : services.isError ? (
        <DeferredNotice title="Service unreachable">{(services.error as Error).message}</DeferredNotice>
      ) : (
        <>
          <div data-testid="agency-health-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(byAgency).map(([agency, count]) => {
              const h = healthMap[agency];
              const tone = h ? healthTone(h.status) : { dot: 'bg-gray-300', text: 'text-gray-500' };
              return (
                <div key={agency} className="bg-white rounded-xl border border-border p-5" data-testid={`agency-card-${agency}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">{agency}</p>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${tone.text}`}>
                      <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
                      {h?.status ?? 'NO DATA'}
                    </span>
                  </div>
                  <p className="text-xs text-muted mt-1">{count} service(s)</p>
                  {h ? (
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <dt className="text-muted">24h requests</dt>
                        <dd className="text-gray-900 font-mono">{h.totalRequests}</dd>
                      </div>
                      <div>
                        <dt className="text-muted">Failures</dt>
                        <dd className={`font-mono ${h.recentFailures > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
                          {h.recentFailures}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted">Success rate</dt>
                        <dd className="text-gray-900 font-mono">{Math.round(h.successRate * 100)}%</dd>
                      </div>
                      <div>
                        <dt className="text-muted">Last request</dt>
                        <dd className="text-gray-900 text-[11px]">
                          {h.lastRequestAt ? new Date(h.lastRequestAt).toLocaleTimeString() : '—'}
                        </dd>
                      </div>
                    </dl>
                  ) : null}
                </div>
              );
            })}
            {Object.keys(byAgency).length === 0 ? (
              <p className="text-sm text-muted">No services in catalogue.</p>
            ) : null}
          </div>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Catalogue</h2>
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Code</th>
                    <th className="text-left px-4 py-2">Service</th>
                    <th className="text-left px-4 py-2">Agency</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {services.data?.items.map((s) => (
                    <tr key={s.code} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-xs">{s.code}</td>
                      <td className="px-4 py-2 text-xs text-gray-700">{s.displayName}</td>
                      <td className="px-4 py-2 text-xs text-gray-700">{s.agency}</td>
                      <td className="px-4 py-2"><StatusBadge status={s.active ? 'ACTIVE' : 'INACTIVE'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
